import { FiCheck, FiX, FiLink, FiCpu } from "react-icons/fi";
import type {
	AIDuplicateGroup,
	ReviewClarification,
} from "../../../../types/clarification-review.type";

interface AIDuplicateGroupCardProps {
	group: AIDuplicateGroup;
	clarifications: ReviewClarification[];
	onAccept: (group: AIDuplicateGroup) => void;
	onDismiss: (mainId: number) => void;
	isDismissed: boolean;
	isAccepted: boolean;
}

export const AIDuplicateGroupCard: React.FC<AIDuplicateGroupCardProps> = ({
	group,
	clarifications,
	onAccept,
	onDismiss,
	isDismissed,
	isAccepted,
}) => {
	const isResolved = isDismissed || isAccepted;
	const mainClarification = clarifications.find((c) => c.id === group.suggested_main_id);
	const duplicateClarifications = clarifications.filter((c) =>
		group.duplicate_ids.includes(c.id)
	);
	const confidencePercent = Math.round(group.confidence * 100);

	return (
		<div
			className={`rounded-lg border p-3 ${
				isResolved ? "border-gray-200 bg-gray-50 opacity-60" : "border-indigo-200 bg-white"
			}`}
			data-testid={`ai-duplicate-group-${group.suggested_main_id}`}
		>
			<div className="mb-2 flex items-center justify-between">
				<div className="flex items-center gap-2">
					<FiLink className="text-indigo-500" size={14} />
					<span className="text-sm font-medium text-gray-700">
						Duplicate Group ({group.duplicate_ids.length + 1} items)
					</span>
					<span className="text-xs text-gray-400">{confidencePercent}% confidence</span>
				</div>
				{!isResolved && (
					<div className="flex gap-1">
						<button
							type="button"
							className="inline-flex items-center gap-1 rounded-md border border-green-300 bg-white px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-50"
							onClick={() => onAccept(group)}
							data-testid={`ai-dup-accept-${group.suggested_main_id}`}
						>
							<FiCheck size={12} />
							Link
						</button>
						<button
							type="button"
							className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-500 hover:bg-gray-50"
							onClick={() => onDismiss(group.suggested_main_id)}
							data-testid={`ai-dup-dismiss-${group.suggested_main_id}`}
						>
							<FiX size={12} />
							Dismiss
						</button>
					</div>
				)}
			</div>

			{/* Reasoning */}
			<div className="mb-2 flex items-start gap-1.5">
				<FiCpu className="mt-0.5 shrink-0 text-indigo-400" size={12} />
				<p className="text-xs text-gray-500">{group.reasoning}</p>
			</div>

			{/* Main clarification */}
			{mainClarification && (
				<div className="mb-1.5 rounded bg-indigo-50 px-2 py-1.5">
					<span className="text-xs font-medium text-indigo-700">
						Main: #{mainClarification.reference_number}
					</span>
					<p className="mt-0.5 text-xs text-gray-600">
						{mainClarification.clarification_text.length > 120
							? `${mainClarification.clarification_text.substring(0, 120)}...`
							: mainClarification.clarification_text}
					</p>
				</div>
			)}

			{/* Duplicate clarifications */}
			{duplicateClarifications.map((dup) => (
				<div key={dup.id} className="mb-1 rounded bg-gray-50 px-2 py-1.5">
					<span className="text-xs font-medium text-gray-500">
						Duplicate: #{dup.reference_number}
					</span>
					<p className="mt-0.5 text-xs text-gray-500">
						{dup.clarification_text.length > 120
							? `${dup.clarification_text.substring(0, 120)}...`
							: dup.clarification_text}
					</p>
				</div>
			))}

			{/* Status */}
			{isAccepted && (
				<span className="mt-1 inline-block text-xs font-medium text-green-600">Linked</span>
			)}
			{isDismissed && (
				<span className="mt-1 inline-block text-xs font-medium text-gray-400">Dismissed</span>
			)}
		</div>
	);
};
